import RoomRoundedIcon from '@mui/icons-material/RoomRounded';
import { GoogleOption } from '#service/autocomplete-service/option';

const addressSelector = (label, address) => {
  const city = address.find(a => a.types.includes('locality'));
  return city ? label.split(`, ${city.long_name}`)[0] : undefined;
};

const fieldSelector = (keyNames, address) => {
  const component = address.find(a => keyNames.find(k => a.types.includes(k))) || {};
  return component.long_name || undefined;
};

const addressFields = [
  { id: 1, name: 'address1', label: 'Address 1', value: addressSelector },
  { id: 2, name: 'address2', label: 'Address 2' },
  { id: 3, name: 'city', label: 'City', value: fieldSelector, key: ['locality'] },
  { id: 4, name: 'state', label: 'State', value: fieldSelector, key: ['administrative_area_level_1', 'administrative_area_level_2'] },
  { id: 5, name: 'country', label: 'Country', value: fieldSelector, key: ['country'] },
  { id: 6, name: 'zipCode', label: 'Zip code', value: fieldSelector, key: ['postal_code'] }
];

class GoogleOptionsService {
  #autocompleteService;

  #placesService;

  constructor(autocompleteService, placesService) {
    this.#autocompleteService = autocompleteService;
    this.#placesService = placesService;
  }

  async getAddressPredictions(value, { signal } = {}) {
    const abortErrorMessage = 'Failed to execute \'getAddressPredictions\' The user aborted a request';

    if (signal?.aborted) {
      throw new DOMException(abortErrorMessage, 'AbortError');
    }

    return new Promise((resolve, reject) => {
      try {
        this.#autocompleteService.getPlacePredictions({ input: value, types: ['address'] }, (results, status) => {
          if (status !== 'OK') {
            reject(new Error(status));
          } else if (signal?.aborted) {
            reject(new DOMException(abortErrorMessage, 'AbortError'));
          } else {
            resolve(results);
          }
        })
        /**
         * getPlacePredictions method from gMaps API mixes promise based
         * and callback based approaches at the same time.
         * Thus, we have to handle both types of errors.
         */
          .catch(e => {
            reject(e);
          });
      } catch (e) {
        reject(e);
      }
    });
  }

  async getAddressAndLocationByPlaceIdRaw(placeId) {
    return new Promise((resolve, reject) => {
      try {
        this.#placesService.getDetails({ placeId, fields: ['address_component', 'geometry.location'] }, (result, status) => {
          if (status !== 'OK') {
            reject(new Error(status));
          }
          resolve(result);
        });
      } catch (e) {
        reject(e);
      }
    });
  }

  static transformAddressAndLocation = (
    formattedAddress,
    { address_components: addressComponents, geometry }
  ) => {
    const address = addressFields.reduce((a, c) => {
      if (!c.value) {
        return a;
      }
      return {
        ...a,
        [c.name]: c.key ?
          c.value(c.key, addressComponents) :
          c.value(formattedAddress, addressComponents)
      };
    }, {});

    const location = { lat: geometry.location.lat(), lng: geometry.location.lng() };

    return { address, locations: [location] };
  };

  async getOptions(value, fetchOptions) {
    if (!value) {
      return [];
    }

    const predictions = await this.getAddressPredictions(value, fetchOptions);
    if (Array.isArray(predictions)) {
      return predictions.map(prediction =>
        new GoogleOption(
          prediction.description,
          RoomRoundedIcon,
          prediction.place_id,
          this
        ));
    }

    return [];
  }

  async getAddressAndLocationDetails(placeId, description) {
    const addressAndLocationRaw = await this.getAddressAndLocationByPlaceIdRaw(placeId);
    return GoogleOptionsService.transformAddressAndLocation(description, addressAndLocationRaw);
  }
}

export default GoogleOptionsService;
